import type {
  Coding,
  Encounter,
  Identifier,
  Observation,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import type { JSONContent } from '@tiptap/core'
import type { TransactionMutationBuilder } from '#src/bundle-builder.ts'
import {
  type NodeAttrs,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '#src/observation-logic/documentUnits.ts'
import {
  type ConditionEnv,
  evaluateCondition,
} from '#src/observation-logic/evaluate.ts'
import {
  panelDerivedRule,
  panelOverrides,
  withDerivedAnswers,
} from '#src/observation-logic/instruments/derivedItems.ts'
import {
  buildInstrumentObservations,
  type InstrumentAnswers,
  instrumentAnswerKey,
  OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM,
} from '#src/observation-logic/instruments/records.ts'
import {
  fhirObservationValue,
  type ObservationValue,
} from '#src/observation-logic/value.ts'
import { OBSERVATION_MODE_LATEST } from '#src/observationReferences.ts'
import { panelTemplate } from '#src/terminology/instruments/loincPanels.ts'
import { instrumentItems } from '#src/terminology/instruments/registry.ts'
import type { InstrumentTemplate } from '#src/terminology/instruments/types.ts'

/* A note whose observation records are made when it is signed or submitted
 * keeps each answer in its node until then: an observation node's `draft`
 * (`drafts` for a repeating choice), an instrument panel's `answers`. This
 * module reads those answers out of the tiptap JSON, says which required
 * questions are still unanswered, and writes the records. The editor and the
 * server share it, so both agree on what a note will record. */

// Ties a record to the note node it was made from. One per record:
// `<note>#<node>`, and `<note>#<node>#<answer code>` for each answer of a
// repeating choice. Writing by it makes signing again update the same record.
export const NOTE_NODE_IDENTIFIER_SYSTEM = 'urn:seen:note-node'

// Ties every record a note made to the note, so signing again can retract
// those the note no longer holds.
export const NOTE_CAPTURE_IDENTIFIER_SYSTEM = 'urn:seen:note-capture'

// What a derived draft's `derivedFrom` holds in place of a reference before
// its sources are written: the source node's id.
export const NOTE_NODE_REFERENCE_PREFIX = 'note-node:'

// How a held panel answer is named as a computed node's source: the panel
// node and the item's member key, rewritten at signing to the item's record.
export function panelItemSourceId(panelId: string, member: string): string {
  return `${panelId}|${member}`
}

export function noteNodeKey(
  noteKey: string,
  nodeId: string,
  answerCode?: string
): string {
  return answerCode
    ? `${noteKey}#${nodeId}#${answerCode}`
    : `${noteKey}#${nodeId}`
}

function isObservation(value: unknown): value is Observation {
  return (
    !!value &&
    typeof value === 'object' &&
    (value as { resourceType?: unknown }).resourceType === 'Observation'
  )
}

// The draft an observation node holds, when it holds one.
export function nodeDraft(attrs: NodeAttrs): Observation | null {
  return isObservation(attrs.draft) ? attrs.draft : null
}

// A repeating choice's drafts, one per chosen answer.
export function nodeDrafts(attrs: NodeAttrs): Observation[] {
  return Array.isArray(attrs.drafts) ? attrs.drafts.filter(isObservation) : []
}

// The answer a node holds in the note: its draft's value, or a repeating
// choice's chosen codes together. Null when it holds none.
export function draftValue(attrs: NodeAttrs): ObservationValue | null {
  if (attrs.mode === OBSERVATION_MODE_LATEST) {
    return null
  }
  const drafts = nodeDrafts(attrs)
  if (drafts.length) {
    const value = drafts.flatMap((draft) => {
      const answer = fhirObservationValue(draft)
      return answer?.kind === 'coding' ? answer.value : []
    })
    return value.length ? { kind: 'coding', value } : null
  }
  const draft = nodeDraft(attrs)
  return draft ? fhirObservationValue(draft) : null
}

// Whether the node holds an answer in the note, as a draft. A node frozen
// to the latest value holds none: a draft left from before it was frozen
// is neither read nor written.
export function hasDraft(attrs: NodeAttrs): boolean {
  return (
    attrs.mode !== OBSERVATION_MODE_LATEST &&
    (!!nodeDraft(attrs) || nodeDrafts(attrs).length > 0)
  )
}

// A panel's answers held in the note.
export function panelAnswers(attrs: NodeAttrs): InstrumentAnswers | null {
  const answers = attrs.answers
  return answers && typeof answers === 'object' && !Array.isArray(answers)
    ? (answers as InstrumentAnswers)
    : null
}

// The items an instrument's own conditions hide given these answers (an
// item asked only after another is answered a certain way). A condition
// that cannot be told yet leaves its item shown.
export function instrumentHiddenItems(
  template: InstrumentTemplate,
  answers: InstrumentAnswers,
  variable: ConditionEnv['variable']
): Coding[] {
  const items = instrumentItems(template)
  const env: ConditionEnv = {
    variable,
    observation: (operand) => {
      const item = items.find(
        (candidate) =>
          candidate.coding.code === operand.code &&
          (!operand.system || candidate.coding.system === operand.system)
      )
      return item
        ? (answers[instrumentAnswerKey(item.coding)] ?? null)
        : 'missing'
    },
  }
  return template.sections.flatMap((section) => {
    const sectionHidden =
      !!section.showWhen && evaluateCondition(section.showWhen, env) === false
    return section.items.flatMap((item) =>
      sectionHidden ||
      (item.showWhen && evaluateCondition(item.showWhen, env) === false)
        ? [item.coding]
        : []
    )
  })
}

// Which nodes and sections the note hides, by id: those whose show-when
// evaluates false. Anything not decided shows.
export type HiddenIds = ReadonlySet<string>

export interface RequiredGap {
  // The node's id in the note.
  id: string
  // What the note calls the question, for a list of what is missing.
  label: string
  // For a panel: its shown items still unanswered.
  unanswered?: number
}

const NO_VARIABLE: ConditionEnv['variable'] = () => 'unavailable'

function nodeLabel(attrs: NodeAttrs): string {
  for (const key of ['codingDisplay', 'display', 'name', 'code']) {
    const value = attrs[key]
    if (typeof value === 'string' && value.trim()) {
      return value.replace(/^@/, '')
    }
  }
  return 'Observation'
}

function isAnswered(attrs: NodeAttrs): boolean {
  return (
    attrs.mode === OBSERVATION_MODE_LATEST ||
    hasDraft(attrs) ||
    (typeof attrs.reference === 'string' && !!attrs.reference) ||
    (Array.isArray(attrs.references) && attrs.references.length > 0)
  )
}

function panelUnanswered(
  attrs: NodeAttrs,
  template: InstrumentTemplate,
  variable: ConditionEnv['variable']
): number {
  const answers = panelAnswers(attrs) ?? {}
  const hidden = new Set(
    instrumentHiddenItems(template, answers, variable).map(instrumentAnswerKey)
  )
  return instrumentItems(template).filter((item) => {
    const key = instrumentAnswerKey(item.coding)
    return !hidden.has(key) && answers[key] == null
  }).length
}

// Shown required questions with no answer, in note order. A node frozen to
// the latest value counts as answered; a panel needs every item it shows.
export function requiredGaps(
  doc: JSONContent,
  hidden: HiddenIds,
  variable: ConditionEnv['variable'] = NO_VARIABLE
): RequiredGap[] {
  const gaps: RequiredGap[] = []
  const visit = (node: JSONContent): void => {
    const attrs = node.attrs ?? {}
    const id = typeof attrs.id === 'string' ? attrs.id : null
    if (id && hidden.has(id)) {
      return
    }
    if (node.type === OBSERVATION_NODE) {
      if (id && attrs.required && !isAnswered(attrs)) {
        gaps.push({ id, label: nodeLabel(attrs) })
      }
      return
    }
    if (node.type === OBSERVATION_PANEL_NODE) {
      const template = panelTemplate(attrs)
      if (
        id &&
        template &&
        attrs.required &&
        attrs.mode !== OBSERVATION_MODE_LATEST
      ) {
        const unanswered = panelUnanswered(attrs, template, variable)
        if (unanswered > 0) {
          gaps.push({ id, label: template.title, unanswered })
        }
      }
      return
    }
    for (const child of node.content ?? []) {
      visit(child)
    }
  }
  visit(doc)
  return gaps
}

export interface NoteCaptureContext {
  // The note's identity: `Composition/<id>`, or the key a note not yet
  // created was given.
  noteKey: string
  subject: Reference<Patient>
  encounter?: Reference<Encounter>
  // Who signed or submitted, for records whose draft names no performer and
  // for instrument panels.
  performer: Reference<Practitioner>
  status: 'preliminary' | 'final'
  // When the note's records were taken: its encounter's start, else now.
  effectiveDateTime?: string
  variable?: ConditionEnv['variable']
  // The records an earlier submission of this note wrote, by key (see
  // `noteCapturesByKey`). Each is updated in place and every other record
  // created: a write matched by identifier is conditional, and Medplum
  // refuses a transaction of more than 8 entries holding one.
  existing?: ReadonlyMap<string, Observation>
}

// The records a note wrote, by the key each was written under.
export function noteCapturesByKey(
  observations: readonly Observation[]
): Map<string, Observation> {
  return new Map(
    observations.flatMap((observation) => {
      const key = noteNodeKeyOf(observation)
      return key ? [[key, observation] as const] : []
    })
  )
}

export interface NoteCaptureWrite {
  // Every `NOTE_NODE_IDENTIFIER_SYSTEM` key written, for retracting the rest.
  keys: Set<string>
  // The bundle reference each node's record was written under, by node id.
  fullUrls: Map<string, string>
  // Every record to file on the note, by bundle reference: each node's,
  // each answer of a repeating choice, each panel's panel record.
  filed: string[]
}

function withoutOurIdentifiers(identifier: Identifier[] | undefined) {
  return (identifier ?? []).filter(
    (entry) =>
      entry.system !== NOTE_NODE_IDENTIFIER_SYSTEM &&
      entry.system !== NOTE_CAPTURE_IDENTIFIER_SYSTEM
  )
}

function noteIdentifiers(ctx: NoteCaptureContext, key: string): Identifier[] {
  return [
    { system: NOTE_NODE_IDENTIFIER_SYSTEM, value: key },
    { system: NOTE_CAPTURE_IDENTIFIER_SYSTEM, value: ctx.noteKey },
  ]
}

function writeByKey(
  builder: TransactionMutationBuilder,
  resource: Observation,
  ctx: NoteCaptureContext,
  key: string,
  fullUrl?: string
): string {
  const keyed: Observation = {
    ...resource,
    identifier: [
      ...withoutOurIdentifiers(resource.identifier),
      ...noteIdentifiers(ctx, key),
    ],
  }
  const overrides = fullUrl ? { fullUrl, request: {} } : undefined
  const prior = ctx.existing?.get(key)
  return (
    (prior?.id
      ? builder.update({ ...keyed, id: prior.id }, overrides)
      : builder.create(keyed, overrides)
    ).reference ?? ''
  )
}

function fromDraft(draft: Observation, ctx: NoteCaptureContext): Observation {
  const { id: _id, meta: _meta, ...rest } = draft
  const observation: Observation = {
    ...rest,
    status: ctx.status,
    subject: ctx.subject,
    performer: draft.performer?.length ? draft.performer : [ctx.performer],
    effectiveDateTime:
      draft.effectiveDateTime ??
      ctx.effectiveDateTime ??
      new Date().toISOString(),
  }
  if (ctx.encounter) {
    observation.encounter = ctx.encounter
  }
  return observation
}

function isDerivedDraft(draft: Observation): boolean {
  return (draft.derivedFrom ?? []).some((source) =>
    source.reference?.startsWith(NOTE_NODE_REFERENCE_PREFIX)
  )
}

/* Adds the records a note's answers make to `builder`, one per shown answer,
 * written by `NOTE_NODE_IDENTIFIER_SYSTEM` key so signing again updates them.
 * A derived draft is written after its sources, its `derivedFrom` pointed at
 * their records. Hidden nodes, nodes frozen to the latest value and nodes
 * whose records were already written as they were answered are left out. */
export function addNoteCaptures(
  builder: TransactionMutationBuilder,
  doc: JSONContent,
  hidden: HiddenIds,
  ctx: NoteCaptureContext
): NoteCaptureWrite {
  const keys = new Set<string>()
  const fullUrls = new Map<string, string>()
  const filed: string[] = []
  const derived: { id: string; draft: Observation }[] = []

  const writeNode = (id: string, attrs: NodeAttrs): void => {
    const drafts = nodeDrafts(attrs)
    if (drafts.length) {
      for (const draft of drafts) {
        const code = draft.valueCodeableConcept?.coding?.[0]?.code
        if (!code) {
          continue
        }
        const key = noteNodeKey(ctx.noteKey, id, code)
        keys.add(key)
        filed.push(writeByKey(builder, fromDraft(draft, ctx), ctx, key))
      }
      return
    }
    const draft = nodeDraft(attrs)
    if (!draft) {
      return
    }
    if (isDerivedDraft(draft)) {
      derived.push({ id, draft })
      return
    }
    const key = noteNodeKey(ctx.noteKey, id)
    keys.add(key)
    const fullUrl = writeByKey(builder, fromDraft(draft, ctx), ctx, key)
    fullUrls.set(id, fullUrl)
    filed.push(fullUrl)
  }

  const writePanel = (id: string, attrs: NodeAttrs): void => {
    const template = panelTemplate(attrs)
    const held = panelAnswers(attrs)
    if (!template || !held) {
      return
    }
    const variable = ctx.variable ?? NO_VARIABLE
    const overrides = panelOverrides(attrs)
    const answers = withDerivedAnswers(template, held, { overrides, variable })
    const groupId =
      typeof attrs.groupId === 'string' && attrs.groupId ? attrs.groupId : id
    const built = buildInstrumentObservations({
      template,
      answers,
      subject: ctx.subject,
      performer: ctx.performer,
      status: ctx.status,
      groupId,
      effectiveDateTime: ctx.effectiveDateTime || undefined,
      hidden: instrumentHiddenItems(template, answers, variable),
      calculatedRule: (item) =>
        panelDerivedRule(template, item.coding, overrides),
    })
    for (const entry of [built.panel, ...built.items, ...built.scores]) {
      const member = entry.resource.identifier?.find(
        (identifier) =>
          identifier.system === OBSERVATION_GROUP_MEMBER_IDENTIFIER_SYSTEM
      )?.value
      if (!member) {
        continue
      }
      const key = noteNodeKey(ctx.noteKey, id, member)
      keys.add(key)
      // A computed node reading this item's held answer names it so.
      fullUrls.set(panelItemSourceId(id, member), entry.fullUrl)
      writeByKey(
        builder,
        ctx.encounter
          ? { ...entry.resource, encounter: ctx.encounter }
          : entry.resource,
        ctx,
        key,
        entry.fullUrl
      )
    }
    fullUrls.set(id, built.panel.fullUrl)
    filed.push(built.panel.fullUrl)
  }

  const visit = (node: JSONContent): void => {
    const attrs = node.attrs ?? {}
    const id = typeof attrs.id === 'string' ? attrs.id : null
    if (id && hidden.has(id)) {
      return
    }
    if (attrs.mode === OBSERVATION_MODE_LATEST) {
      return
    }
    if (node.type === OBSERVATION_NODE) {
      if (id) {
        writeNode(id, attrs)
      }
      return
    }
    if (node.type === OBSERVATION_PANEL_NODE) {
      if (id) {
        writePanel(id, attrs)
      }
      return
    }
    for (const child of node.content ?? []) {
      visit(child)
    }
  }
  visit(doc)

  for (const { id, draft } of derived) {
    const derivedFrom = (draft.derivedFrom ?? []).flatMap((source) => {
      const sourceId = source.reference?.startsWith(NOTE_NODE_REFERENCE_PREFIX)
        ? source.reference.slice(NOTE_NODE_REFERENCE_PREFIX.length)
        : null
      if (sourceId === null) {
        return [source]
      }
      const fullUrl = fullUrls.get(sourceId)
      return fullUrl ? [{ ...source, reference: fullUrl }] : []
    })
    const key = noteNodeKey(ctx.noteKey, id)
    keys.add(key)
    const fullUrl = writeByKey(
      builder,
      {
        ...fromDraft(draft, ctx),
        ...(derivedFrom.length ? { derivedFrom } : { derivedFrom: undefined }),
      },
      ctx,
      key
    )
    fullUrls.set(id, fullUrl)
    filed.push(fullUrl)
  }
  return { keys, fullUrls, filed }
}

// The search for every record a note made, to retract those `keys` no
// longer holds after it was edited and signed again.
export function noteCaptureQuery(noteKey: string, subject: Reference<Patient>) {
  return {
    identifier: `${NOTE_CAPTURE_IDENTIFIER_SYSTEM}|${noteKey}`,
    patient: subject.reference ?? '',
    'status:not': 'entered-in-error',
  }
}

// The key a record was written under, when a note made it.
export function noteNodeKeyOf(observation: Observation): string | null {
  return (
    observation.identifier?.find(
      (entry) => entry.system === NOTE_NODE_IDENTIFIER_SYSTEM
    )?.value ?? null
  )
}
